/**
 * Overlay-entry lease — the shared answer to "does an overlay currently own a
 * history entry?".
 *
 * Why this exists (the configurator bug):
 *   Our overlays implement "browser-Back closes the overlay" by pushing a
 *   same-URL history entry on open and popping it again on close. Either way the
 *   entry is eventually popped, and the parent Snackys template answers that pop
 *   by re-rendering the product area: `jtl.article.js` stamps the current entry
 *   with `{a, a2, url, variations}` on product-detail pages and calls
 *   `setArticleContent(...)` on ANY pop that lands on a state-carrying entry. It
 *   cannot distinguish "the user pressed Back" from "an overlay tidied up its own
 *   entry", so every overlay close re-fetched `#result-wrapper` and wiped the
 *   configurator selection the customer had just made — the article became
 *   impossible to add to the cart.
 *
 *   `article-rerender-guard.ts` suppresses that re-render for exactly as long as
 *   an overlay owns an entry. This module is the signal it reads: every overlay
 *   that pushes an entry takes a lease and releases it once the entry is gone.
 *   Correctness comes from counting rather than from sequencing, so stacked
 *   overlays and sibling hand-offs need no special cases.
 *
 * Why the count lives on the DOM, not in a module variable:
 *   Each file under `src-js/` is its own esbuild entry point (gulpfile `js:build`),
 *   so `modal.js` and `cart-dropdown-history-back.js` are separate IIFE bundles
 *   with separate module scopes. A module-level counter would give each bundle its
 *   own private tally and the guard would never see the mini-cart's lease. A
 *   `data-` attribute on `<html>` is the one place both bundles agree on — and it
 *   is readable in devtools, which makes a stuck lease diagnosable in production
 *   instead of invisible.
 *
 * @see src-components/history/article-rerender-guard.ts (the consumer)
 * @see src-components/modal/overlay-close-driver.ts (the back-to-close state machine)
 * @see docs/architecture/modals/target-architecture.md §3.2.3
 */

import type { Unbind } from '../utils/unbind';

/** `<html data-datte-overlay-entries="N">` — N = overlays currently holding an entry. */
const LEASE_ATTR = 'datteOverlayEntries';

/**
 * State tags our own pushed entries carry. An entry bearing one of these is an
 * overlay entry of ours, never a page the parent should re-render for.
 * `datteOverlayBack` is stamped by `overlay-close-driver.ts`, `datteCartBack` by
 * the mini-cart's own state machine.
 */
const OWN_ENTRY_TAGS = ['datteOverlayBack', 'datteCartBack'] as const;

function readCount(): number {
  const raw = document.documentElement.dataset[LEASE_ATTR];
  const n = raw ? Number.parseInt(raw, 10) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function writeCount(n: number): void {
  if (n > 0) {
    document.documentElement.dataset[LEASE_ATTR] = String(n);
  } else {
    delete document.documentElement.dataset[LEASE_ATTR];
  }
}

/**
 * Declare that an overlay now owns a history entry. Call immediately BEFORE
 * pushing it. The returned release is idempotent, so a driver may call it from
 * more than one exit path without double-decrementing.
 */
export function acquireOverlayEntryLease(): Unbind {
  writeCount(readCount() + 1);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    writeCount(readCount() - 1);
  };
}

/**
 * Release a lease one task after the pop that retired the entry.
 *
 * The deferral matters because the parent re-registers its popstate listener on
 * every AJAX content load (`global.js:343`, `listing.js:89` both re-run
 * `onLoad()`), so parent listeners can be registered after ours and still run in
 * the same dispatch. Holding the lease until the dispatch is over covers all of
 * them. A task rather than a microtask, whose checkpoint runs between two
 * listeners of one dispatch; and rather than `requestAnimationFrame`, which is
 * paused entirely while the document is hidden.
 *
 * Releasing late is harmless — the worst case is one suppressed re-render that
 * the customer did not ask for — which is why a counter tolerates the slack that
 * a history-rewriting scheme could not.
 */
export function releaseAfterPop(release: Unbind): void {
  setTimeout(release, 0);
}

/** True while any overlay owns a history entry. */
export function hasOverlayEntryPending(): boolean {
  return readCount() > 0;
}

/**
 * True when `state` is one of our own overlay entries. Used to cover the FORWARD
 * direction: a popped overlay entry stays in forward history, and traversing onto
 * it hands the parent a truthy state whose article fields are all undefined —
 * `loadContent(undefined)` then re-fetches the current URL and wipes the page
 * just as a Back-press used to.
 */
export function isOwnOverlayEntry(state: unknown): boolean {
  if (typeof state !== 'object' || state === null) return false;
  return OWN_ENTRY_TAGS.some((tag) => tag in state);
}

/** Test seam: drop any lease left behind by a previous case. */
export function resetOverlayEntryLeasesForTest(): void {
  writeCount(0);
}
